import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, getBackendUrl } from './services/apiClient';
import './ApplyPage.css';

// Single fallback for apply form theme (browser-independent; used when API fails).
const APPLY_FORM_FALLBACK = {
    form_background_color: '#f5f5f5',
    form_text_color: '#333333',
    form_primary_color: '#667eea',
    form_show_job_title: true,
    form_show_job_description: true,
    form_show_linkedin_field: true,
    form_require_google_auth: false,
};

const PRE_RESUME_TEXTS = new Set([
    'Phone number',
    'City',
    'State',
    'Cover letter (optional)',
]);

const WORK_ELIGIBILITY_TEXTS = new Set([
    'Are you legally authorized to work in the U.S.?',
    'Do you now, or will you in the future, require sponsorship for employment authorization in the United States?',
    'Do you consent to a background check?',
    'Do you meet the eligibility requirements for obtaining a U.S. Government Security Clearance?',
    'Preferred work arrangement',
]);

const EEO_TEXTS = new Set([
    'Gender',
    'Gender (EEO)',
    'Gender (voluntary EEO)',
    'Race / Ethnicity',
    'Race / Ethnicity (voluntary EEO)',
    'Veteran status',
    'Veteran status (voluntary)',
    'Disability status',
    'Disability status (voluntary)',
]);

const EXPANDABLE_HELP_TEXTS = new Set([
    'Veteran status',
    'Veteran status (voluntary)',
    'Disability status',
    'Disability status (voluntary)',
]);

const EEO_SECTION_INTRO =
    'Voluntary self identification questions from the employer. Any questions you answer will be shared separately from your application and used for reporting only. Fields marked with an asterisk (*) are required.';

const ExpandableHelp = ({ text, previewChars = 280 }) => {
    const [expanded, setExpanded] = useState(false);
    if (!text) return null;
    const needsToggle = text.length > previewChars;
    const shown = !needsToggle || expanded ? text : `${text.slice(0, previewChars).trim()}…`;
    return (
        <div className="apply-expandable-help">
            <p className="apply-expandable-help__text">{shown}</p>
            {needsToggle && (
                <button
                    type="button"
                    className="apply-expandable-help__toggle"
                    onClick={() => setExpanded((v) => !v)}
                >
                    {expanded ? 'Show less' : 'Show more'}
                </button>
            )}
        </div>
    );
};

const ApplyPage = () => {
    const { jobId, slug } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [job, setJob] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [formSettings, setFormSettings] = useState(null);
    const [applicantToken, setApplicantToken] = useState(() => searchParams.get('applicant_token') || '');
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        resume: null,
        first_name: '',
        last_name: '',
        candidate_name: '',
        candidate_email: '',
        linkedin_profile_url: '',
        answers: {}
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [applicationsOpen, setApplicationsOpen] = useState(true);

    const isSlugRoute = Boolean(slug);

    const { preQuestions, workQuestions, eeoQuestions, disclosureQuestions, otherPostQuestions } = useMemo(() => {
        const pre = [];
        const work = [];
        const eeo = [];
        const disclosure = [];
        const other = [];
        for (const q of questions) {
            const text = q.question_text || '';
            if (PRE_RESUME_TEXTS.has(text)) {
                pre.push(q);
            } else if (WORK_ELIGIBILITY_TEXTS.has(text)) {
                work.push(q);
            } else if (EEO_TEXTS.has(text)) {
                eeo.push(q);
            } else if (text.startsWith('I certify that the information provided')) {
                disclosure.push(q);
            } else if (!PRE_RESUME_TEXTS.has(text)) {
                other.push(q);
            }
        }
        return {
            preQuestions: pre,
            workQuestions: work,
            eeoQuestions: eeo,
            disclosureQuestions: disclosure,
            otherPostQuestions: other,
        };
    }, [questions]);

    useEffect(() => {
        const qpToken = searchParams.get('applicant_token');
        const hash = window.location.hash || '';
        const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
        const hashToken = hashParams.get('applicant_token');

        const token = qpToken || hashToken;
        if (token) {
            setApplicantToken(token);
            try {
                window.history.replaceState({}, document.title, window.location.pathname);
            } catch {
                // ignore
            }
        }
    }, [searchParams]);

    useEffect(() => {
        if (!slug && !jobId) return;
        let cancelled = false;
        const loadApplyContext = async () => {
            const url = isSlugRoute
                ? `${getBackendUrl()}/api/apply-context/by-slug/${encodeURIComponent(slug)}`
                : `${getBackendUrl()}/api/jobs/${jobId}/apply-context`;
            try {
                const response = await fetch(url);
                if (cancelled) return;
                if (response.ok) {
                    const data = await response.json();
                    setJob(data.job ?? null);
                    const fs = data.form_settings || {};
                    setFormSettings({
                        ...APPLY_FORM_FALLBACK,
                        ...fs,
                        form_require_google_auth: false,
                    });
                    setQuestions(data.questions ?? []);
                    setApplicationsOpen(data.applications_open !== false);
                } else {
                    setJob(null);
                    setFormSettings(APPLY_FORM_FALLBACK);
                    setQuestions([]);
                    setApplicationsOpen(true);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Error loading apply form:', err);
                    setJob(null);
                    setFormSettings(APPLY_FORM_FALLBACK);
                    setQuestions([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadApplyContext();
        return () => { cancelled = true; };
    }, [jobId, slug, isSlugRoute]);

    const handleAnswerChange = (questionId, value) => {
        setFormData((prev) => ({
            ...prev,
            answers: {
                ...prev.answers,
                [questionId]: value
            }
        }));
        if (errors[`question_${questionId}`]) {
            setErrors((prev) => ({
                ...prev,
                [`question_${questionId}`]: null
            }));
        }
    };

    const fullName = () =>
        `${(formData.first_name || '').trim()} ${(formData.last_name || '').trim()}`.trim();

    const validateStep1 = () => {
        const newErrors = {};
        if (!(formData.first_name || '').trim()) newErrors.first_name = 'First name is required';
        if (!(formData.last_name || '').trim()) newErrors.last_name = 'Last name is required';
        if (!(formData.candidate_email || '').trim()) {
            newErrors.candidate_email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.candidate_email.trim())) {
            newErrors.candidate_email = 'Enter a valid email address';
        }
        if (!formData.resume) newErrors.resume = 'Resume is required';

        preQuestions.forEach((question) => {
            if (!question.is_required) return;
            const answer = formData.answers[question.id];
            if (!answer || (Array.isArray(answer) && answer.length === 0) || answer.toString().trim() === '') {
                newErrors[`question_${question.id}`] = 'This field is required';
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = () => {
        const newErrors = {};
        const postQuestions = [
            ...workQuestions,
            ...eeoQuestions,
            ...disclosureQuestions,
            ...otherPostQuestions,
        ];
        postQuestions.forEach((question) => {
            if (!question.is_required) return;
            const answer = formData.answers[question.id];
            if (!answer || (Array.isArray(answer) && answer.length === 0) || answer.toString().trim() === '') {
                newErrors[`question_${question.id}`] = 'This question is required';
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleContinue = (e) => {
        e.preventDefault();
        if (!validateStep1()) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setFormData((prev) => ({ ...prev, candidate_name: fullName() }));
        setStep(2);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateStep2()) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setSubmitting(true);
        const name = fullName() || formData.candidate_name;

        const formDataToSend = new FormData();
        formDataToSend.append('resume_file', formData.resume);
        formDataToSend.append('job_description', job?.description || '');
        formDataToSend.append('job_id', job?.id ?? jobId);
        formDataToSend.append('candidate_name', name);
        formDataToSend.append('candidate_email', formData.candidate_email);
        formDataToSend.append('linkedin_profile_url', formData.linkedin_profile_url);
        if (activeSettings.form_require_google_auth && applicantToken) {
            formDataToSend.append('applicant_token', applicantToken);
        }

        Object.keys(formData.answers).forEach((questionId) => {
            const answer = formData.answers[questionId];
            if (Array.isArray(answer)) {
                answer.forEach((value) => {
                    formDataToSend.append(`question_${questionId}`, value);
                });
            } else {
                formDataToSend.append(`question_${questionId}`, answer);
            }
        });

        try {
            const response = await apiFetch('/api/linkedin/application', {
                method: 'POST',
                body: formDataToSend
            });

            const result = await response.json();
            if (response.status === 409) {
                if (isSlugRoute) {
                    navigate(`/apply/t/${slug}/already-applied`);
                } else {
                    navigate(`/apply/${jobId}/already-applied`);
                }
                return;
            }
            if (response.status === 403) {
                const closedMsg = result.detail || result.error
                    || 'This position is no longer accepting applications.';
                alert(typeof closedMsg === 'string' ? closedMsg : JSON.stringify(closedMsg));
                return;
            }
            if (result.success) {
                const trackState = result.tracking_token
                    ? { trackingToken: result.tracking_token }
                    : undefined;
                if (isSlugRoute) {
                    navigate(`/apply/t/${slug}/thank-you`, { state: trackState });
                } else {
                    navigate(`/apply/${jobId}/thank-you`, { state: trackState });
                }
            } else {
                const msg = result.detail || result.error || 'Failed to submit application. Please try again.';
                alert(Array.isArray(msg) ? msg.join(' ') : typeof msg === 'object' ? JSON.stringify(msg) : msg);
            }
        } catch (error) {
            console.error('Error submitting application:', error);
            alert('An error occurred while submitting your application. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderQuestionInput = (question) => {
        const value = formData.answers[question.id] || (question.question_type === 'checkbox' ? [] : '');
        const error = errors[`question_${question.id}`];

        switch (question.question_type) {
            case 'number':
                return (
                    <input
                        type="number"
                        value={value}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder={question.placeholder}
                        className={error ? 'error' : ''}
                        min="0"
                    />
                );

            case 'multiple_choice':
                return (
                    <div className="radio-group">
                        {question.options?.map((option, idx) => (
                            <label key={idx} className="radio-label">
                                <input
                                    type="radio"
                                    name={`question_${question.id}`}
                                    value={option}
                                    checked={value === option}
                                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                                />
                                <span className="choice-mark" aria-hidden="true" />
                                <span className="choice-text">{option}</span>
                            </label>
                        ))}
                    </div>
                );

            case 'dropdown':
                return (
                    <select
                        value={value}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        className={error ? 'error' : ''}
                    >
                        <option value="">{question.placeholder || 'Select an option'}</option>
                        {question.options?.map((option, idx) => (
                            <option key={idx} value={option}>{option}</option>
                        ))}
                    </select>
                );

            case 'checkbox': {
                const selectedValues = Array.isArray(value) ? value : [];
                return (
                    <div className="checkbox-group">
                        {question.options?.map((option, idx) => (
                            <label key={idx} className="checkbox-label">
                                <input
                                    type="checkbox"
                                    value={option}
                                    checked={selectedValues.includes(option)}
                                    onChange={(e) => {
                                        const newValues = e.target.checked
                                            ? [...selectedValues, option]
                                            : selectedValues.filter((v) => v !== option);
                                        handleAnswerChange(question.id, newValues);
                                    }}
                                />
                                <span className="choice-mark" aria-hidden="true" />
                                <span className="choice-text">{option}</span>
                            </label>
                        ))}
                    </div>
                );
            }

            default:
                return (
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                        placeholder={question.placeholder}
                        className={error ? 'error' : ''}
                    />
                );
        }
    };

    const renderQuestionBlock = (question, { expandable = false } = {}) => (
        <div key={question.id} className="form-group">
            <label>
                {question.question_text}
                {question.is_required && <span className="required-star"> *</span>}
            </label>
            {question.help_text && (
                expandable || EXPANDABLE_HELP_TEXTS.has(question.question_text) ? (
                    <ExpandableHelp text={question.help_text} />
                ) : (
                    <small className="help-text">{question.help_text}</small>
                )
            )}
            {renderQuestionInput(question)}
            {errors[`question_${question.id}`] && (
                <span className="error-text">{errors[`question_${question.id}`]}</span>
            )}
        </div>
    );

    const findPre = (text) => preQuestions.find((q) => q.question_text === text);

    const extractJobTitle = (description) => {
        if (!description) return 'Job Application';
        const lines = description.split('\n');
        if (lines.length > 0) {
            let firstLine = lines[0].trim();
            firstLine = firstLine.replace(/^File:\s*/i, '').replace(/^Job description from file:\s*/i, '').trim();
            if (firstLine && firstLine.length < 200) {
                return firstLine;
            }
        }
        return 'Job Application';
    };

    if (loading) {
        return (
            <div className="apply-page">
                <div className="loading">Loading application form...</div>
            </div>
        );
    }

    const jobTitle = job ? extractJobTitle(job.description) : 'Job Application';
    const activeSettings = formSettings || APPLY_FORM_FALLBACK;

    if (!applicationsOpen) {
        return (
            <div className="apply-page" style={{ backgroundColor: '#0c001f', color: activeSettings.form_text_color || '#e0e0e0', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
                <div className="apply-container" style={{ textAlign: 'center', maxWidth: '560px' }}>
                    <h1 className="job-title">{jobTitle}</h1>
                    <p style={{ marginTop: '1rem', lineHeight: 1.6 }}>
                        This position is no longer accepting applications. The maximum number of candidates for this role has been reached.
                    </p>
                </div>
            </div>
        );
    }

    const backgroundStyle = activeSettings.form_background_color === '#f5f5f5' || !activeSettings.form_background_color
        ? {
            backgroundColor: '#0c001f',
            backgroundImage: `
                radial-gradient(at 20% 20%, hsla(28,100%,74%,0.15) 0px, transparent 50%),
                radial-gradient(at 80% 20%, hsla(217,100%,50%,0.15) 0px, transparent 50%),
                radial-gradient(at 80% 80%, hsla(339,100%,50%,0.15) 0px, transparent 50%),
                radial-gradient(at 20% 80%, hsla(140,100%,50%,0.15) 0px, transparent 50%)
            `
          }
        : { backgroundColor: activeSettings.form_background_color };

    const phoneQ = findPre('Phone number');
    const cityQ = findPre('City');
    const stateQ = findPre('State');
    const coverQ = findPre('Cover letter (optional)');

    return (
        <div
            className="apply-page"
            style={{
                ...backgroundStyle,
                color: activeSettings.form_text_color
            }}
        >
            <div className="apply-container">
                {activeSettings.form_show_job_title && (
                    <h1 className="job-title">{jobTitle}</h1>
                )}

                {activeSettings.form_show_job_description && job?.description && step === 1 && (
                    <div className="job-description">
                        <h3>Job Description</h3>
                        <div className="description-content">
                            {job.description.split('\n').map((line, idx) => (
                                <p key={idx}>{line || <br />}</p>
                            ))}
                        </div>
                    </div>
                )}

                {step === 1 ? (
                    <form onSubmit={handleContinue} className="application-form apply-step-form">
                        <div className="apply-card">
                            <h2 className="apply-card-title">Start your application</h2>
                            <p className="apply-card-subtitle">Enter your name and email to continue.</p>

                            <div className="apply-name-row">
                                <div className="form-group">
                                    <label>First name <span className="required-star">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.first_name}
                                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                                        className={errors.first_name ? 'error' : ''}
                                        autoComplete="given-name"
                                    />
                                    {errors.first_name && <span className="error-text">{errors.first_name}</span>}
                                </div>
                                <div className="form-group">
                                    <label>Last name <span className="required-star">*</span></label>
                                    <input
                                        type="text"
                                        value={formData.last_name}
                                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                        className={errors.last_name ? 'error' : ''}
                                        autoComplete="family-name"
                                    />
                                    {errors.last_name && <span className="error-text">{errors.last_name}</span>}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Email address <span className="required-star">*</span></label>
                                <input
                                    type="email"
                                    value={formData.candidate_email}
                                    onChange={(e) => setFormData({ ...formData, candidate_email: e.target.value })}
                                    className={errors.candidate_email ? 'error' : ''}
                                    autoComplete="email"
                                />
                                {errors.candidate_email && <span className="error-text">{errors.candidate_email}</span>}
                            </div>
                        </div>

                        <div className="apply-card">
                            <h3 className="apply-section-heading">Basic information</h3>
                            {phoneQ && renderQuestionBlock(phoneQ)}
                            <div className="apply-city-state-row">
                                {cityQ && renderQuestionBlock(cityQ)}
                                {stateQ && renderQuestionBlock(stateQ)}
                            </div>
                            {activeSettings.form_show_linkedin_field && (
                                <div className="form-group">
                                    <label>
                                        LinkedIn Profile URL{' '}
                                        <span className="optional-note">(Optional)</span>
                                    </label>
                                    <input
                                        type="url"
                                        value={formData.linkedin_profile_url}
                                        onChange={(e) => setFormData({ ...formData, linkedin_profile_url: e.target.value })}
                                        placeholder="https://linkedin.com/in/yourprofile"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="apply-card apply-upload-card">
                            <h3 className="apply-section-heading">Upload Resume and other docs like Cover Letter</h3>
                            <div className="apply-upload-panel">
                                <div className="apply-upload-icon" aria-hidden="true">↑</div>
                                <div>
                                    <strong>Upload your résumé</strong>
                                    <p className="apply-upload-hint">We read your résumé and fill in the fields automatically.</p>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>
                                    Resume <span className="required-star">*</span>
                                </label>
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    onChange={(e) => setFormData({ ...formData, resume: e.target.files[0] })}
                                    className={errors.resume ? 'error' : ''}
                                />
                                {formData.resume && (
                                    <small className="help-text">Selected: {formData.resume.name}</small>
                                )}
                                {errors.resume && <span className="error-text">{errors.resume}</span>}
                            </div>
                            {coverQ && renderQuestionBlock(coverQ)}
                        </div>

                        <div className="form-actions">
                            <button
                                type="submit"
                                className="submit-button"
                                style={{ backgroundColor: activeSettings.form_primary_color || '#667eea' }}
                            >
                                Continue
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={handleSubmit} className="application-form apply-step-form">
                        <div className="apply-step-banner">
                            <button type="button" className="apply-back-link" onClick={() => setStep(1)}>
                                ← Back
                            </button>
                            <span>Resume attached{formData.resume ? `: ${formData.resume.name}` : ''}</span>
                        </div>

                        <div className="apply-card">
                            <h2 className="apply-card-title">Work Eligibility &amp; Compliance</h2>
                            {workQuestions.map((q) => renderQuestionBlock(q))}
                        </div>

                        <div className="apply-card">
                            <h2 className="apply-card-title">Voluntary EEOC Information</h2>
                            <p className="apply-card-subtitle">Required for compliance reporting setup — answers are voluntary unless marked.</p>
                            <p className="help-text apply-eeo-intro">{EEO_SECTION_INTRO}</p>
                            {eeoQuestions.map((q) => renderQuestionBlock(q, { expandable: EXPANDABLE_HELP_TEXTS.has(q.question_text) }))}
                        </div>

                        {otherPostQuestions.length > 0 && (
                            <div className="apply-card">
                                <h2 className="apply-card-title">Additional questions</h2>
                                {otherPostQuestions.map((q) => renderQuestionBlock(q))}
                            </div>
                        )}

                        <div className="apply-card">
                            <h2 className="apply-card-title">Disclosures &amp; Acknowledgments</h2>
                            {disclosureQuestions.map((q) => renderQuestionBlock(q))}
                        </div>

                        <div className="form-actions">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="submit-button"
                                style={{ backgroundColor: activeSettings.form_primary_color || '#667eea' }}
                            >
                                {submitting ? 'Submitting...' : 'Submit'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ApplyPage;
